/**
 * 主入口文件 - 首页
 */

import { ParticleBackground } from './ParticleBackground.js';
import { SnakeGame } from './SnakeGame.js';
import { PlayerSnakeGame } from './PlayerSnakeGame.js';
import { ThemeToggle } from './ThemeToggle.js';
import { Router } from './Router.js';
import { debounce } from './utils.js';

// 时间线数据
interface TimelineItem {
  date: string;
  content: string;
}

const timelineData: TimelineItem[] = [
  { date: '', content: '敬请期待' },
  { date: '2025.11.10', content: '阿尼亚个人博客上线' },
  { date: '2025.10.31', content: 'Any聊天文件传输上线' },
  { date: '2024.09.30', content: 'ICP备案成功' },
  { date: '2024.09.19', content: '注册域名ipv4.website' },
  { date: '2024.07.03', content: '搭建第一个网站' },
];

class HomePage {
  constructor() {
    new Router();
    this.init();
  }

  /**
   * 初始化
   */
  private init(): void {
    this.initParticleBackground();
    this.initSnakeGame();
    this.initPlayerSnakeGame();
    this.initThemeToggle();
    this.initSponsorModal();
    this.renderTimeline();
    this.initScrollEffects();
  }

  /**
   * 初始化粒子背景
   */
  private initParticleBackground(): void {
    try {
      new ParticleBackground('particle-canvas');
    } catch (error) {
      console.error('Failed to initialize particle background:', error);
    }
  }

  /**
   * 初始化贪吃蛇游戏
   */
  private initSnakeGame(): void {
    try {
      const game = new SnakeGame('snake-game');
      
      // 设置目标长度滑块
      const targetLengthSlider = document.getElementById('target-length') as HTMLInputElement;
      const targetLengthValue = document.getElementById('target-length-value');
      
      if (targetLengthSlider && targetLengthValue) {
        targetLengthSlider.addEventListener('input', (e) => {
          const value = (e.target as HTMLInputElement).value;
          targetLengthValue.textContent = value;
          game.setTargetLength(parseInt(value));
        });
      }
      
      // 奖励值设置滑块
      const deathRewardSlider = document.getElementById('reward-death') as HTMLInputElement;
      const foodRewardSlider = document.getElementById('reward-food') as HTMLInputElement;
      const surviveRewardSlider = document.getElementById('reward-survive') as HTMLInputElement;
      
      const deathRewardValue = document.getElementById('reward-death-value');
      const foodRewardValue = document.getElementById('reward-food-value');
      const surviveRewardValue = document.getElementById('reward-survive-value');
      
      if (deathRewardSlider && deathRewardValue) {
        deathRewardSlider.addEventListener('input', (e) => {
          const value = parseFloat((e.target as HTMLInputElement).value);
          deathRewardValue.textContent = value.toString();
          
          const food = foodRewardSlider ? parseFloat(foodRewardSlider.value) : 10;
          const survive = surviveRewardSlider ? parseFloat(surviveRewardSlider.value) : 0.1;
          game.setRewardValues(value, food, survive);
        });
      }
      
      if (foodRewardSlider && foodRewardValue) {
        foodRewardSlider.addEventListener('input', (e) => {
          const value = parseFloat((e.target as HTMLInputElement).value);
          foodRewardValue.textContent = value.toString();
          
          const death = deathRewardSlider ? parseFloat(deathRewardSlider.value) : -128;
          const survive = surviveRewardSlider ? parseFloat(surviveRewardSlider.value) : 0.1;
          game.setRewardValues(death, value, survive);
        });
      }
      
      if (surviveRewardSlider && surviveRewardValue) {
        surviveRewardSlider.addEventListener('input', (e) => {
          const value = parseFloat((e.target as HTMLInputElement).value);
          surviveRewardValue.textContent = value.toFixed(2);
          
          const death = deathRewardSlider ? parseFloat(deathRewardSlider.value) : -128;
          const food = foodRewardSlider ? parseFloat(foodRewardSlider.value) : 16;
          game.setRewardValues(death, food, value);
        });
      }
      
      // 重置奖励值按钮
      const resetRewardsBtn = document.getElementById('reset-rewards');
      if (resetRewardsBtn) {
        resetRewardsBtn.addEventListener('click', () => {
          game.resetRewardValues();
          // 更新滑块位置
          if (deathRewardSlider) deathRewardSlider.value = '-128';
          if (foodRewardSlider) foodRewardSlider.value = '16';
          if (surviveRewardSlider) surviveRewardSlider.value = '0.1';
        });
      }
      
      // 重置学习参数按钮
      const resetParamsBtn = document.getElementById('reset-params');
      if (resetParamsBtn) {
        resetParamsBtn.addEventListener('click', () => {
          if (confirm('确定要重置学习参数吗？这将清除参数学习历史。')) {
            game.resetParams();
          }
        });
      }
      
      // 重置所有学习数据按钮
      const resetBtn = document.getElementById('reset-learning');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          if (confirm('确定要清空所有学习数据吗？这将清除统计、Q表和参数历史。')) {
            game.resetLearning();
          }
        });
      }
    } catch (error) {
      console.error('Failed to initialize snake game:', error);
    }
  }

  /**
   * 初始化玩家贪吃蛇游戏
   */
  private initPlayerSnakeGame(): void {
    try {
      const playerGame = new PlayerSnakeGame('player-snake-game');
      
      // 从AI游戏获取目标长度并同步到玩家游戏
      const targetLengthSlider = document.getElementById('target-length') as HTMLInputElement;
      if (targetLengthSlider) {
        const initialTarget = parseInt(targetLengthSlider.value);
        playerGame.setTargetLength(initialTarget);
        
        // 监听目标长度变化，同步到玩家游戏
        targetLengthSlider.addEventListener('input', (e) => {
          const value = parseInt((e.target as HTMLInputElement).value);
          playerGame.setTargetLength(value);
        });
      }
    } catch (error) {
      console.error('Failed to initialize player snake game:', error);
    }
  }

  /**
   * 初始化主题切换
   */
  private initThemeToggle(): void {
    try {
      new ThemeToggle('theme-toggle');
    } catch (error) {
      console.error('Failed to initialize theme toggle:', error);
    }
  }

  /**
   * 初始化赞助弹窗
   */
  private initSponsorModal(): void {
    const sponsorBtn = document.getElementById('sponsor-btn');
    const modal = document.getElementById('sponsor-modal');

    if (!sponsorBtn || !modal) {
      console.warn('Sponsor modal elements not found');
      return;
    }

    // 强制确保初始状态是隐藏的
    modal.classList.remove('active');
    modal.style.display = 'none';
    document.body.style.overflow = '';

    // 关闭弹窗函数
    const closeModal = () => {
      modal.classList.remove('active');
      modal.style.display = 'none';
      document.body.style.overflow = '';
    };

    // 打开弹窗函数
    const openModal = () => {
      modal.style.display = 'flex';
      // 使用 setTimeout 确保 display 先生效
      setTimeout(() => {
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
      }, 10);
    };

    // 绑定打开按钮
    sponsorBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openModal();
    });

    // 使用事件委托绑定关闭事件
    modal.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      
      // 点击遮罩层关闭
      if (target.classList.contains('modal-overlay')) {
        e.preventDefault();
        e.stopPropagation();
        closeModal();
        return;
      }
      
      // 点击关闭按钮关闭
      if (target.classList.contains('modal-close') || target.closest('.modal-close')) {
        e.preventDefault();
        e.stopPropagation();
        closeModal();
        return;
      }
    });

    // ESC 键关闭
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        closeModal();
      }
    });
  }

  /**
   * 渲染时间线
   */
  private renderTimeline(): void {
    const timeline = document.getElementById('timeline');
    if (!timeline) return;

    timeline.innerHTML = timelineData
      .map(
        (item) => `
        <div class="timeline-item animate-slide-left">
          <div class="timeline-date">${item.date}</div>
          <div class="timeline-content">${item.content}</div>
        </div>
      `
      )
      .join('');
  }

  /**
   * 初始化滚动效果
   */
  private initScrollEffects(): void {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.feature-card, .timeline-item').forEach((el) => {
      observer.observe(el);
    });
  }
}

// 页面加载完成后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // 确保弹窗初始隐藏
    const modal = document.getElementById('sponsor-modal');
    if (modal) {
      modal.classList.remove('active');
      modal.style.display = 'none';
    }
    new HomePage();
  });
} else {
  // 确保弹窗初始隐藏
  const modal = document.getElementById('sponsor-modal');
  if (modal) {
    modal.classList.remove('active');
    modal.style.display = 'none';
  }
  new HomePage();
}
